import React from 'react';
import { Box, Typography, Container, Paper, Divider } from '@mui/material';
import { PageHeader } from '../components/common/PageHeader';
import SEO from '../components/SEO';
import './PrivacyPolicyPage.css';

export const PrivacyPolicyPage: React.FC = () => {
  return (
    <>
      {/* SEO Meta Tags */}
      <SEO
        title="Privacy Policy - Cycling Buddy"
        description="Privacy policy and data protection information for Cycling Buddy, the intelligent bike route planner. Learn how we protect your data and respect your privacy."
        keywords="privacy policy, data protection, cycling buddy, bike route planner, GDPR, privacy"
        url="https://cycling-buddy.app/privacy"
        canonical="https://cycling-buddy.app/privacy"
        type="article"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Privacy Policy - Cycling Buddy",
          "description": "Privacy policy and data protection information for Cycling Buddy",
          "author": {
            "@type": "Organization",
            "name": "Cycling Buddy"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Cycling Buddy",
            "logo": {
              "@type": "ImageObject",
              "url": "https://cycling-buddy.app/android-chrome-512x512.png"
            }
          },
          "dateModified": "2025-10-05",
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": "https://cycling-buddy.app/privacy"
          }
        }}
      />
      
      <div className="privacy-policy-page">
      <Container maxWidth="md" className="privacy-policy-container">
        <Paper elevation={2} className="privacy-policy-paper">
          {/* Header with back button and title */}
          <PageHeader title="Privacy Policy / Datenschutzerklärung" titleVariant="h5" />

          {/* English Section */}
          <Typography 
            variant="h4" 
            component="h2" 
            className="privacy-policy-section-title"
            sx={{ mb: 3, color: 'primary.main', mt: 2 }}
          >
            🇬🇧 English
          </Typography>

          <Typography 
            variant="h5" 
            component="h3" 
            className="privacy-policy-section-title"
          >
            1. Privacy at a Glance
          </Typography>
        
          <Typography 
            variant="h6" 
            component="h4" 
            className="privacy-policy-subsection-title"
          >
            General Information
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            The following information provides an overview of what happens to your personal data when you 
            use this application. Personal data is any data that can be used to identify you personally.
          </Typography>

          <Typography 
            variant="h6" 
            component="h4" 
            className="privacy-policy-subsection-title"
          >
            Data Collection in this Application
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            <strong>Who is responsible for data collection in this application?</strong>
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Data processing in this application is carried out by the application operator. 
            Contact details can be found in the legal notice section of this application.
          </Typography>

          <Typography 
            variant="h5" 
            component="h3" 
            className="privacy-policy-section-title"
          >
            2. Data Storage & Authentication
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            This application uses <strong>Supabase</strong> as its backend infrastructure for:
          </Typography>
          <Box 
            component="ul" 
            className="privacy-policy-text"
            sx={{ ml: 2 }}
          >
            <li><strong>User Authentication:</strong> Secure login and registration</li>
            <li><strong>Route Storage:</strong> Saving your cycling routes and preferences</li>
            <li><strong>Database:</strong> PostgreSQL database for data persistence</li>
            <li><strong>File Storage:</strong> Route thumbnails and elevation profiles</li>
          </Box>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Supabase is hosted in the EU and complies with GDPR regulations. Data is encrypted in transit and at rest.
          </Typography>

          <Typography 
            variant="h5" 
            component="h3" 
            className="privacy-policy-section-title"
          >
            3. External Services & APIs
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            This application uses the following external services for enhanced functionality:
          </Typography>
          <Box 
            component="ul" 
            className="privacy-policy-text"
            sx={{ ml: 2 }}
          >
            <li><strong>OpenStreetMap/Leaflet:</strong> Map display and visualization</li>
            <li><strong>BRouter API:</strong> Cycling route calculation and optimization</li>
            <li><strong>Nominatim API:</strong> Address geocoding and reverse geocoding</li>
            <li><strong>Google OAuth:</strong> Optional authentication via Google account</li>
          </Box>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            When using these services, minimal necessary data (such as coordinates for routing) 
            may be transmitted to the respective providers. No personal information is shared 
            without your explicit consent.
          </Typography>

          <Typography 
            variant="h5" 
            component="h3" 
            className="privacy-policy-section-title"
          >
            4. Local Data Storage
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            The application stores route data and preferences locally in your browser's 
            storage for offline functionality and improved performance. This data never 
            leaves your device unless you explicitly choose to sync it with your account.
          </Typography>

          <Typography 
            variant="h5" 
            component="h3" 
            className="privacy-policy-section-title"
          >
            5. User Accounts & Authentication
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            When you create an account, we collect:
          </Typography>
          <Box 
            component="ul" 
            className="privacy-policy-text"
            sx={{ ml: 2 }}
          >
            <li>Email address (for account identification and communication)</li>
            <li>Encrypted password (never stored in plain text)</li>
            <li>Account creation and last login timestamps</li>
            <li>User preferences and settings</li>
          </Box>

          <Typography 
            variant="h5" 
            component="h3" 
            className="privacy-policy-section-title"
          >
            6. Your Rights
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            You have the right to:
          </Typography>
          <Box 
            component="ul" 
            className="privacy-policy-text"
            sx={{ ml: 2 }}
          >
            <li>Access your stored personal data</li>
            <li>Correct inaccurate personal data</li>
            <li>Delete your personal data</li>
            <li>Restrict data processing</li>
            <li>Data portability</li>
            <li>Object to processing</li>
          </Box>

          <Typography 
            variant="h5" 
            component="h3" 
            className="privacy-policy-section-title"
          >
            7. Analytics & Performance Tracking
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            This application uses privacy-friendly analytics to improve performance and user experience:
          </Typography>

          <Typography 
            variant="h6" 
            component="h4" 
            className="privacy-policy-subsection-title"
          >
            7.1 Plausible Analytics
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            We use Plausible Analytics for basic website traffic analysis:
          </Typography>
          <Box 
            component="ul" 
            className="privacy-policy-text"
            sx={{ ml: 2 }}
          >
            <li><strong>Purpose:</strong> Understand general usage patterns and popular pages</li>
            <li><strong>Data collected:</strong> Page views, referrer information, general location (country-level)</li>
            <li><strong>Privacy features:</strong> No cookies, no cross-site tracking, no personal data</li>
            <li><strong>Data retention:</strong> 2 years</li>
            <li><strong>GDPR compliance:</strong> Full compliance, hosted in EU</li>
          </Box>

          <Typography 
            variant="h6" 
            component="h4" 
            className="privacy-policy-subsection-title"
          >
            7.2 Application Performance Analytics
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            We collect anonymous technical metrics to improve app performance:
          </Typography>
          <Box 
            component="ul" 
            className="privacy-policy-text"
            sx={{ ml: 2 }}
          >
            <li><strong>Route calculation metrics:</strong> Points count, distance, elevation, calculation time</li>
            <li><strong>File operation metrics:</strong> GPX file sizes and processing performance (no file content)</li>
            <li><strong>Technical information:</strong> Browser type, device platform, app version</li>
            <li><strong>Session tracking:</strong> Anonymous session IDs (not linked to user accounts)</li>
          </Box>

          <Typography 
            variant="h6" 
            component="h4" 
            className="privacy-policy-subsection-title"
          >
            7.3 What We DON'T Track
          </Typography>
          <Box 
            component="ul" 
            className="privacy-policy-text"
            sx={{ ml: 2 }}
          >
            <li>❌ Personal identifying information</li>
            <li>❌ Route names, content, or locations</li>
            <li>❌ File names or file content</li>
            <li>❌ IP addresses or precise location data</li>
            <li>❌ User accounts or login information</li>
            <li>❌ Cross-session user tracking</li>
          </Box>

          <Typography 
            variant="h6" 
            component="h4" 
            className="privacy-policy-subsection-title"
          >
            7.4 Data Usage & Retention
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Analytics data is used solely for improving route calculation performance, 
            optimizing app compatibility across devices, identifying technical issues, 
            and understanding general usage patterns.
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            <strong>Data retention:</strong> Performance metrics (1 year), Plausible analytics (2 years). 
            Data is automatically deleted after the retention period.
          </Typography>

          <Typography 
            variant="h5" 
            component="h3" 
            className="privacy-policy-section-title"
          >
            8. Data Security
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            We implement appropriate technical and organizational measures to protect 
            your personal data against unauthorized access, alteration, disclosure, or destruction. 
            All data transmission is encrypted using modern security protocols.
          </Typography>

          <Divider className="privacy-policy-divider" sx={{ my: 4 }} />

          {/* German Section */}
          <Typography 
            variant="h4" 
            component="h2" 
            className="privacy-policy-section-title"
            sx={{ mb: 3, color: 'primary.main' }}
          >
            🇩🇪 Deutsch
          </Typography>
        
          <Typography 
            variant="h5" 
            component="h3" 
            className="privacy-policy-section-title"
          >
            1. Datenschutz auf einen Blick
          </Typography>
        
          <Typography 
            variant="h6" 
            component="h4" 
            className="privacy-policy-subsection-title"
          >
            Allgemeine Hinweise
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren 
            personenbezogenen Daten passiert, wenn Sie diese Anwendung benutzen. Personenbezogene 
            Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
          </Typography>

          <Typography 
            variant="h6" 
            component="h4" 
            className="privacy-policy-subsection-title"
          >
            Datenerfassung in dieser Anwendung
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            <strong>Wer ist verantwortlich für die Datenerfassung in dieser Anwendung?</strong>
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Die Datenverarbeitung in dieser Anwendung erfolgt durch den Anwendungsbetreiber. 
            Dessen Kontaktdaten können Sie dem Impressum dieser Anwendung entnehmen.
          </Typography>

          <Typography 
            variant="h5" 
            component="h3" 
            className="privacy-policy-section-title"
          >
            2. Datenspeicherung & Authentifizierung
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Diese Anwendung nutzt <strong>Supabase</strong> als Backend-Infrastruktur für:
          </Typography>
          <Box 
            component="ul" 
            className="privacy-policy-text"
            sx={{ ml: 2 }}
          >
            <li><strong>Benutzer-Authentifizierung:</strong> Sichere Anmeldung und Registrierung</li>
            <li><strong>Routen-Speicherung:</strong> Speicherung Ihrer Fahrradrouten und Einstellungen</li>
            <li><strong>Datenbank:</strong> PostgreSQL-Datenbank für Datenpersistierung</li>
            <li><strong>Dateispeicherung:</strong> Routen-Thumbnails und Höhenprofile</li>
          </Box>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Supabase wird in der EU gehostet und entspricht den DSGVO-Bestimmungen. Daten werden bei der Übertragung und Speicherung verschlüsselt.
          </Typography>

          <Typography 
            variant="h5" 
            component="h3" 
            className="privacy-policy-section-title"
          >
            3. Externe Dienste & APIs
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Diese Anwendung nutzt folgende externe Dienste für erweiterte Funktionalität:
          </Typography>
          <Box 
            component="ul" 
            className="privacy-policy-text"
            sx={{ ml: 2 }}
          >
            <li><strong>OpenStreetMap/Leaflet:</strong> Kartendarstellung und Visualisierung</li>
            <li><strong>BRouter API:</strong> Fahrradrouten-Berechnung und -Optimierung</li>
            <li><strong>Nominatim API:</strong> Adress-Geokodierung und Reverse-Geokodierung</li>
            <li><strong>Google OAuth:</strong> Optionale Authentifizierung über Google-Konto</li>
          </Box>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Bei der Nutzung dieser Dienste können minimal notwendige Daten (wie Koordinaten für 
            die Routenberechnung) an die jeweiligen Anbieter übertragen werden. Keine persönlichen 
            Informationen werden ohne Ihre ausdrückliche Zustimmung weitergegeben.
          </Typography>

          <Typography 
            variant="h5" 
            component="h3" 
            className="privacy-policy-section-title"
          >
            4. Lokale Datenspeicherung
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Die Anwendung speichert Routendaten und Einstellungen lokal im Speicher Ihres Browsers 
            für Offline-Funktionalität und verbesserte Performance. Diese Daten verlassen Ihr Gerät 
            nicht, es sei denn, Sie entscheiden sich ausdrücklich dafür, sie mit Ihrem Konto zu synchronisieren.
          </Typography>

          <Typography 
            variant="h5" 
            component="h3" 
            className="privacy-policy-section-title"
          >
            5. Benutzerkonten & Authentifizierung
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Wenn Sie ein Konto erstellen, erheben wir:
          </Typography>
          <Box 
            component="ul" 
            className="privacy-policy-text"
            sx={{ ml: 2 }}
          >
            <li>E-Mail-Adresse (für Konto-Identifikation und Kommunikation)</li>
            <li>Verschlüsseltes Passwort (niemals im Klartext gespeichert)</li>
            <li>Zeitstempel der Konto-Erstellung und letzten Anmeldung</li>
            <li>Benutzereinstellungen und -präferenzen</li>
          </Box>

          <Typography 
            variant="h5" 
            component="h3" 
            className="privacy-policy-section-title"
          >
            6. Ihre Rechte
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Sie haben jederzeit das Recht:
          </Typography>
          <Box 
            component="ul" 
            className="privacy-policy-text"
            sx={{ ml: 2 }}
          >
            <li>Auskunft über Ihre gespeicherten personenbezogenen Daten zu erhalten</li>
            <li>Berichtigung unrichtiger personenbezogener Daten zu verlangen</li>
            <li>Löschung Ihrer personenbezogenen Daten zu verlangen</li>
            <li>Einschränkung der Datenverarbeitung zu verlangen</li>
            <li>Datenübertragbarkeit zu verlangen</li>
            <li>Der Verarbeitung zu widersprechen</li>
          </Box>

          <Typography 
            variant="h5" 
            component="h3" 
            className="privacy-policy-section-title"
          >
            7. Analytics & Leistungsmessung
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Diese Anwendung nutzt datenschutzfreundliche Analyseverfahren zur Verbesserung der Leistung und Benutzererfahrung:
          </Typography>

          <Typography 
            variant="h6" 
            component="h4" 
            className="privacy-policy-subsection-title"
          >
            7.1 Plausible Analytics
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Wir verwenden Plausible Analytics für grundlegende Website-Traffic-Analysen:
          </Typography>
          <Box 
            component="ul" 
            className="privacy-policy-text"
            sx={{ ml: 2 }}
          >
            <li><strong>Zweck:</strong> Verstehen allgemeiner Nutzungsmuster und beliebter Seiten</li>
            <li><strong>Erfasste Daten:</strong> Seitenaufrufe, Referrer-Informationen, allgemeine Standortdaten (Länderebene)</li>
            <li><strong>Datenschutz-Features:</strong> Keine Cookies, kein Cross-Site-Tracking, keine persönlichen Daten</li>
            <li><strong>Datenspeicherung:</strong> 2 Jahre</li>
            <li><strong>DSGVO-Konformität:</strong> Vollständige Konformität, gehostet in der EU</li>
          </Box>

          <Typography 
            variant="h6" 
            component="h4" 
            className="privacy-policy-subsection-title"
          >
            7.2 Anwendungsleistungs-Analytics
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Wir erfassen anonyme technische Metriken zur Verbesserung der App-Leistung:
          </Typography>
          <Box 
            component="ul" 
            className="privacy-policy-text"
            sx={{ ml: 2 }}
          >
            <li><strong>Routenberechnungs-Metriken:</strong> Anzahl Punkte, Entfernung, Höhenmeter, Berechnungszeit</li>
            <li><strong>Dateioperation-Metriken:</strong> GPX-Dateigrößen und Verarbeitungsleistung (kein Dateiinhalt)</li>
            <li><strong>Technische Informationen:</strong> Browser-Typ, Geräteplattform, App-Version</li>
            <li><strong>Session-Tracking:</strong> Anonyme Session-IDs (nicht mit Benutzerkonten verknüpft)</li>
          </Box>

          <Typography 
            variant="h6" 
            component="h4" 
            className="privacy-policy-subsection-title"
          >
            7.3 Was wir NICHT erfassen
          </Typography>
          <Box 
            component="ul" 
            className="privacy-policy-text"
            sx={{ ml: 2 }}
          >
            <li>❌ Persönlich identifizierbare Informationen</li>
            <li>❌ Routennamen, -inhalte oder -standorte</li>
            <li>❌ Dateinamen oder Dateiinhalte</li>
            <li>❌ IP-Adressen oder präzise Standortdaten</li>
            <li>❌ Benutzerkonto- oder Anmeldeinformationen</li>
            <li>❌ Session-übergreifendes Benutzer-Tracking</li>
          </Box>

          <Typography 
            variant="h6" 
            component="h4" 
            className="privacy-policy-subsection-title"
          >
            7.4 Datennutzung & Aufbewahrung
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Analytics-Daten werden ausschließlich zur Verbesserung der Routenberechnungsleistung, 
            Optimierung der App-Kompatibilität zwischen Geräten, Identifizierung technischer Probleme 
            und zum Verstehen allgemeiner Nutzungsmuster verwendet.
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            <strong>Datenspeicherung:</strong> Leistungsmetriken (1 Jahr), Plausible Analytics (2 Jahre). 
            Daten werden nach Ablauf der Aufbewahrungsfrist automatisch gelöscht.
          </Typography>

          <Typography 
            variant="h5" 
            component="h3" 
            className="privacy-policy-section-title"
          >
            8. Datensicherheit
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Wir setzen angemessene technische und organisatorische Maßnahmen um, um Ihre 
            personenbezogenen Daten vor unbefugtem Zugriff, Veränderung, Offenlegung oder 
            Zerstörung zu schützen. Alle Datenübertragungen sind mit modernen Sicherheitsprotokollen verschlüsselt.
          </Typography>

          <Typography 
            variant="h5" 
            component="h3" 
            className="privacy-policy-section-title"
          >
            9. Kontakt
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Bei Fragen zum Datenschutz oder zur Ausübung Ihrer Rechte wenden Sie sich bitte an:
          </Typography>
          <Typography 
            paragraph
            className="privacy-policy-text"
          >
            Mario Krone<br />
            E-Mail: mario@cyclingbuddy.de
          </Typography>

          <Typography 
            className="privacy-policy-text"
            sx={{
              mt: 4, 
              fontStyle: 'italic', 
              color: 'text.secondary'
            }}
          >
            Last updated: {new Date().toLocaleDateString('en-US')} / Letzte Aktualisierung: {new Date().toLocaleDateString('de-DE')}
          </Typography>
        </Paper>
      </Container>
      </div>
    </>
  );
};
